import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

import styles from "./Dialog.module.css";

// `summary` too: a disclosure in the box is a control, and a trap that
// does not know it lets Shift+Tab walk out of the box from there.
const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

interface DialogProps {
  title: string;
  /** What the dialog is about — the entry, the Vorgang („Sockel umlaufend · 2026-0071"). Wired into `aria-describedby`. */
  subtitle?: ReactNode;
  onClose: () => void;
  /**
   * Fired once the dialog's own cleanup has run — after focus has already
   * gone back to the opener. For a "put the focus on X once the dialog is
   * gone" need (task 11 controller addendum): a caller that used to reach
   * for a nested `setTimeout` after `onClose` now has a point in time that
   * actually is after the close, DOM included.
   */
  onClosed?: () => void;
  /**
   * While true, Escape does not close the dialog — a request is in flight
   * and losing the typed text would throw it away (task 11, review focus 3).
   * The caller's own controls (a disabled Cancel button) still guard a click.
   */
  busy?: boolean;
  /**
   * `decision` (default): the size of one decision. `wide`: a picture to look
   * at — a customer's drawing large (Task 18), as wide as the window allows.
   */
  size?: "decision" | "wide";
  children: ReactNode;
}

/**
 * A modal the size of one decision (retract an entry, assign a customer,
 * hand a Vorgang over) — UX ruling 8 (F-11).
 *
 * In a portal on `document.body`: the start page's fading (`.muted`) and the
 * sticky fact sheet make stacking contexts of their own, and a dialog inside
 * them would sit under their neighbours. While open, `#root` is `inert` —
 * nothing behind the dialog can be focused, clicked or read — and the page
 * does not scroll; `scrollbar-gutter: stable` (base.css) keeps it from
 * jumping sideways when the scrollbar goes.
 *
 * Built by hand rather than on `<dialog>.showModal()`: jsdom does not
 * implement it, and the promises are few — focus goes in (to
 * `[data-autofocus]`, else the first control), Tab stays in, Escape closes
 * unless a control inside handled it (`defaultPrevented` — the customer
 * list closes first), and focus goes back to whatever opened it. `onClose`
 * is read through a ref so the mount effect runs once.
 *
 * The opener is read once, during the first render (record fix round 2,
 * I-3) — before any control inside the box exists. Read in the mount effect
 * it could be one: under `<StrictMode>` the effect runs twice, and a child's
 * own focus effect ran between the two runs, so the second run took the
 * customer search for the opener and handed the focus back to it on close —
 * after it had gone with the portal (`<body>`).
 */
export function Dialog({ title, subtitle, onClose, onClosed, busy = false, size = "decision", children }: DialogProps) {
  const titleId = useId();
  const subtitleId = useId();
  const boxRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  const closedRef = useRef(onClosed);
  const busyRef = useRef(busy);
  const [opener] = useState(() => {
    const active = document.activeElement;
    return active instanceof HTMLElement && active !== document.body ? active : null;
  });

  useEffect(() => {
    closeRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    closedRef.current = onClosed;
  }, [onClosed]);

  useEffect(() => {
    busyRef.current = busy;
  }, [busy]);

  useEffect(() => {
    const box = boxRef.current;
    const root = document.getElementById("root");
    const page = document.documentElement;
    const overflow = page.style.overflow;
    root?.setAttribute("inert", "");
    page.style.overflow = "hidden";
    const first = box?.querySelector<HTMLElement>("[data-autofocus]") ?? box?.querySelector<HTMLElement>(FOCUSABLE);
    first?.focus();

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        if (event.defaultPrevented || busyRef.current) return;
        event.preventDefault();
        closeRef.current();
        return;
      }
      // A Tab a control inside already handled (a menu closing on it, the
      // focus put back on its trigger) is not the trap's to answer again.
      if (event.key !== "Tab" || box === null || event.defaultPrevented) return;
      const controls = Array.from(box.querySelectorAll<HTMLElement>(FOCUSABLE));
      const firstControl = controls[0];
      const lastControl = controls[controls.length - 1];
      if (firstControl === undefined || lastControl === undefined) return;
      // Focus fell out of the box — a control that went away under it, a
      // click on the backdrop. `#root` is inert, so the browser's own Tab
      // would wander the page chrome at best; bring it back in (record fix
      // wave I1).
      if (!box.contains(document.activeElement)) {
        event.preventDefault();
        (event.shiftKey ? lastControl : firstControl).focus();
        return;
      }
      if (event.shiftKey && document.activeElement === firstControl) {
        event.preventDefault();
        lastControl.focus();
      } else if (!event.shiftKey && document.activeElement === lastControl) {
        event.preventDefault();
        firstControl.focus();
      }
    }

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      root?.removeAttribute("inert");
      page.style.overflow = overflow;
      opener?.focus();
      closedRef.current?.();
    };
  }, [opener]);

  return createPortal(
    <div className={styles.backdrop}>
      <div
        ref={boxRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={subtitle !== undefined ? subtitleId : undefined}
        className={`${styles.box} ${size === "wide" ? styles.wide : ""}`}
      >
        <header className={styles.head}>
          <h2 id={titleId} className={styles.title}>
            {title}
          </h2>
          {subtitle !== undefined ? (
            <p id={subtitleId} className={styles.subtitle}>
              {subtitle}
            </p>
          ) : null}
        </header>
        <div className={styles.body}>{children}</div>
      </div>
    </div>,
    document.body,
  );
}

/** The dialog's foot: a rule, buttons to the right, the primary one last. Inside the caller's form. */
export function DialogFooter({ children }: { children: ReactNode }) {
  return <div className={styles.footer}>{children}</div>;
}
